import { useCallback } from 'react';
import { getIn } from 'formik';
import { Box, Grid, Typography } from '@mui/material';

import { FProgressBar } from '../../../../../../components/base';
import { disabledFutureDate } from '../../../../../../helpers';
import { FortaContactInfo, SignatureBox } from '../../../../../../components/baseV2';
import {
  TitleBulletList,
  QUESTIONNAIRE_PAGE_BOX_SX,
  QuestionnaireSectionLabel,
} from '../../../../common';
import {
  FormCheckboxField,
  FormDateField,
  FormTextField,
  useFormReadOnly,
} from '../../../../../common';
import { useQuestionnaireFormik } from '../../../../common/hooks';
import { onboardingQuestionnaireActions } from '../../../../../../store';
import { ONBOARDING_TAB_IDS } from '../../../constants';
import { useOnboardingQuestionnaireLoading } from '../../../hooks';
import {
  AGREEMENT_CONSENT_CHECKBOX_FIELDS,
  ONBOARDING_AGREEMENT_SECTIONS,
  ONBOARDING_CONSENT_SECTIONS,
} from './constants';
import {
  AGREEMENT_SIGNATURE_DATE_FIELD_SX,
  AGREEMENT_SIGNATURE_DATE_PICKER_STYLE,
  AGREEMENT_SIGNATURE_TEXT_FIELD_SX,
} from './styles';
import {
  useAgreementInitialValues,
  useSaveAgreementConsentOnChange,
  useSaveAgreementSignatureFields,
} from './hooks';
import { AGREEMENT_FORM_SCHEMA } from './validationSchema';
import { FortaRepresentative } from './components';
import { FORTA_BLUE } from '../../../../../../utils/colors';

export default function AgreementForm() {
  const initialValues = useAgreementInitialValues();
  const isLoading = useOnboardingQuestionnaireLoading();
  const readOnly = useFormReadOnly();
  const formik = useQuestionnaireFormik({
    tabId: ONBOARDING_TAB_IDS.AGREEMENT,
    initialValues,
    validationSchema: AGREEMENT_FORM_SCHEMA,
    updateTabStatus: onboardingQuestionnaireActions.updateOnboardingTabStatus,
  });
  const { saveConsentOnChange, isUpdating } = useSaveAgreementConsentOnChange(
    formik,
    initialValues,
  );
  const { savePrintedNameOnBlur, saveSignatureDateOnChange, saveSignature } =
    useSaveAgreementSignatureFields(formik, initialValues);
  const isFieldDisabled = isLoading || isUpdating || readOnly;
  const handleSignatureValueChange = useCallback(
    (value: string) => {
      void formik.setFieldValue('signature', value);
      void formik.setFieldTouched('signature', true, false);
    },
    [formik],
  );
  const signatureError = getIn(formik.errors, 'signature');
  const signatureErrorText =
    signatureError != null &&
    !Array.isArray(signatureError) &&
    Object.prototype.toString.call(signatureError) === '[object String]'
      ? String(signatureError)
      : undefined;
  const hasSignatureError = Boolean(getIn(formik.touched, 'signature') && signatureErrorText);

  return (
    <>
      <QuestionnaireSectionLabel variant="h5" sx={{ mb: 1.5, color: FORTA_BLUE.primary }}>
        Agreement
      </QuestionnaireSectionLabel>
      <Box sx={QUESTIONNAIRE_PAGE_BOX_SX}>
        <FortaContactInfo />
        <Typography variant="h3" sx={{ my: 4 }}>
          Consent for Electronic Delivery and Communication
        </Typography>
        <Typography sx={{ textAlign: 'left', mb: 1 }}>
          Montera, Inc. d/b/a Forta (collectively with all its affiliates and subsidiaries,
          &quot;Forta&quot;) uses diverse platforms of communication to ensure timely and accurate
          communication is delivered to all stakeholders in the most convenient and efficient way
          possible.
        </Typography>
        <Box sx={{ p: 1 }}>
          {ONBOARDING_CONSENT_SECTIONS.map((section) => (
            <TitleBulletList
              key={section.title}
              title={section.title}
              subtitles={section.subtitles}
              items={section.items}
            />
          ))}
        </Box>
        <Box sx={{ p: 1, pt: 0, mb: 2, position: 'relative', textAlign: 'left' }}>
          <Typography sx={{ fontWeight: 'bold', mb: 2 }}>
            6. Please review the entire agreement. At the end of the agreement, next to the
            signature section, you will find six consent statements. You must review and check all
            six boxes to indicate your consent before signing.
          </Typography>
        </Box>

        <Typography variant="h3" sx={{ my: 4 }}>
          Service Agreement & Consent Form
        </Typography>
        <Typography sx={{ textAlign: 'left', mb: 1 }}>
          This document encompasses important information regarding Montera, Inc. d/b/a Forta&apos;s
          (collectively with all its affiliated entities and subsidiaries, &quot;Forta&quot;)
          practices, policies and professional services (ABA Services) of Applied Behavior Analysis
          (&quot;ABA&quot;). It is necessary to thoroughly read all the information Forta provides
          and ask for clarification at any time. By signing this Service Agreement & Consent Form
          (this &quot;Agreement&quot;), you will agree to and will adhere to this Agreement related
          to the provision for ABA services from Forta.
        </Typography>
        <Typography sx={{ textAlign: 'left', fontWeight: 'bold', mb: 1 }}>
          This Agreement shall become effective upon completion of all required (i) paperwork, (ii)
          consents, and (iii) Clinical Assessments (the &quot;Effective Date&quot;). For avoidance
          of doubt, this Agreement shall become effective no later than the first date of ABA
          Services.
        </Typography>
        <Box sx={{ p: 1 }}>
          {ONBOARDING_AGREEMENT_SECTIONS.map((section) => (
            <TitleBulletList
              key={section.title}
              title={section.title}
              subtitles={section.subtitles}
              items={section.items}
              summary={section.summary}
            />
          ))}
        </Box>
        <Box sx={{ p: 1, textAlign: 'left' }}>
          <Typography sx={{ fontWeight: 'bold', mb: 2 }}>
            Please check each of the boxes below to indicate your consent to the following:
          </Typography>
          {isUpdating ? <FProgressBar /> : null}
          {AGREEMENT_CONSENT_CHECKBOX_FIELDS.map(({ name, label }) => (
            <Box key={name} sx={{ mb: 1.5 }}>
              <FormCheckboxField
                disabled={isFieldDisabled}
                formik={formik}
                label={label}
                name={name}
                onChange={saveConsentOnChange}
              />
            </Box>
          ))}
        </Box>
        <Typography sx={{ textAlign: 'left', fontWeight: 'bold', mb: 1, ml: 1 }}>
          By signing below, you fully and freely agree to Consent for Electronic Delivery and
          Communication and give consent for services to be implemented as proposed.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2, mt: 2 }}>
          <Grid item xs={12} md={6}>
            <FormTextField
              disabled={isFieldDisabled}
              formik={formik}
              label="Printed name"
              name="printed_name"
              onBlur={savePrintedNameOnBlur}
              required
              showFloatingLabel={false}
              showLabelBelow
              showRequiredIndicator={false}
              sx={AGREEMENT_SIGNATURE_TEXT_FIELD_SX}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormDateField
              disabled={isFieldDisabled}
              disabledDate={disabledFutureDate}
              formik={formik}
              label="Signature date"
              name="signature_date"
              onChange={saveSignatureDateOnChange}
              pickerStyle={AGREEMENT_SIGNATURE_DATE_PICKER_STYLE}
              required
              showFloatingLabel={false}
              showLabelBelow
              showRequiredIndicator={false}
              sx={AGREEMENT_SIGNATURE_DATE_FIELD_SX}
            />
          </Grid>
          <Grid item xs={12}>
            <SignatureBox
              message="Signature of Client or Parent/Guardian "
              disabled={isFieldDisabled}
              readOnly={readOnly}
              value={getIn(formik.values, 'signature') ?? ''}
              error={hasSignatureError}
              helperText={hasSignatureError ? signatureErrorText : undefined}
              onValueChange={handleSignatureValueChange}
              onSave={saveSignature}
            />
          </Grid>
          <Grid item xs={12}>
            <FortaRepresentative />
          </Grid>
        </Grid>
        <FortaContactInfo />
      </Box>
    </>
  );
}
